import {
  LayoutGrid,
  BarChart3,
  Users,
  Megaphone,
  Sparkles,
  Settings,
  Search,
  Mic,
  Bell,
  TrendingUp,
} from 'lucide-react';
import BrandLogo from './BrandLogo';

type Row = {
  owner: string;
  status: 'Live' | 'Scheduled' | 'Optimizing' | 'Paused';
  campaign: string;
  channel: string;
  metric: string;
  delta: string;
};

const rows: Row[] = [
  {
    owner: 'Alex K.',
    status: 'Live',
    campaign: 'Summer Launch — Reels',
    channel: 'Instagram',
    metric: 'CTR 4.8%',
    delta: '+12%',
  },
  {
    owner: 'Maria S.',
    status: 'Optimizing',
    campaign: 'Lookalike Retarget',
    channel: 'Meta Ads',
    metric: 'CPA $7.20',
    delta: '-18%',
  },
  {
    owner: 'Priya P.',
    status: 'Live',
    campaign: 'Brand Awareness Push',
    channel: 'TikTok',
    metric: 'Reach 1.2M',
    delta: '+34%',
  },
  {
    owner: 'Jamie L.',
    status: 'Scheduled',
    campaign: 'Newsletter — Weekly',
    channel: 'Email',
    metric: 'Open 41%',
    delta: '+6%',
  },
  {
    owner: 'Dan W.',
    status: 'Live',
    campaign: 'Search — Branded',
    channel: 'Google Ads',
    metric: 'ROAS 4.6x',
    delta: '+22%',
  },
  {
    owner: 'Sam R.',
    status: 'Paused',
    campaign: 'Influencer Wave 3',
    channel: 'YouTube',
    metric: 'Eng 3.1%',
    delta: '—',
  },
  {
    owner: 'Mei H.',
    status: 'Live',
    campaign: 'Carousel — Educate',
    channel: 'LinkedIn',
    metric: 'CTR 5.4%',
    delta: '+9%',
  },
];

const statusStyles: Record<Row['status'], string> = {
  Live: 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30',
  Scheduled: 'bg-sky-500/15 text-sky-300 border border-sky-500/30',
  Optimizing: 'bg-amber-500/15 text-amber-300 border border-amber-500/30',
  Paused: 'bg-zinc-500/15 text-zinc-400 border border-zinc-500/30',
};

const navItems = [
  { icon: LayoutGrid, label: 'Overview', active: true },
  { icon: BarChart3, label: 'Reports' },
  { icon: Megaphone, label: 'Campaigns' },
  { icon: Sparkles, label: 'AI Studio' },
];

const audienceItems = [
  { icon: Users, label: 'Audiences' },
  { icon: TrendingUp, label: 'Insights' },
];

const supportItems = [
  { icon: Bell, label: 'Alerts' },
  { icon: Settings, label: 'Settings' },
];

export default function DashboardMockup({ className }: { className?: string }) {
  return (
    <div
      data-dash-glass
      className={
        'isolate w-[94%] max-w-6xl rounded-2xl border border-white/[0.20] bg-black/20 md:bg-black/[0.08] backdrop-blur-md md:backdrop-blur-2xl shadow-[0_30px_120px_-20px_rgba(0,0,0,0.9),inset_0_1px_0_rgba(255,255,255,0.14)] overflow-hidden ring-1 ring-white/[0.07] transform-gpu ' +
        (className ?? '')
      }
    >
      <div className="grid grid-cols-[210px_1fr]">
        {/* Sidebar — always visible. On mobile the whole dashboard is scaled-fit by Hero,
            so the sidebar comes along for the ride and the layout reads as "desktop dashboard". */}
        <aside className="border-r border-white/10 bg-white/[0.03] p-4">
          <div className="flex items-center gap-2 mb-6">
            <BrandLogo className="h-5 w-5" />
            <span className="text-sm font-semibold tracking-tight text-white">
              SprynAI
            </span>
          </div>

          <NavGroup label="General" items={navItems} />
          <NavGroup label="Audience" items={audienceItems} />
          <NavGroup label="Support" items={supportItems} />
        </aside>

        {/* Main */}
        <div className="p-4 md:p-5">
          {/* Top bar */}
          <div className="flex items-center gap-3 mb-5">
            <h3 className="text-base md:text-lg font-semibold text-white tracking-tight">
              Campaign Overview
            </h3>
            <div className="ml-auto flex items-center gap-2">
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-md bg-white/[0.04] border border-white/10 text-xs text-white/60 w-56">
                <Search className="h-3.5 w-3.5" />
                <span>Search anything...</span>
              </div>
              <button className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-white/10 text-white/60">
                <Mic className="h-3.5 w-3.5" />
              </button>
              <button className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-white/10 text-white/60">
                <Bell className="h-3.5 w-3.5" />
              </button>
              <div className="h-7 w-7 rounded-full bg-gradient-to-br from-fuchsia-400 to-sky-400" />
            </div>
          </div>

          {/* Section title */}
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-white/90">
              Recent Activity Logs
            </h4>
            <div className="flex items-center gap-2 px-2.5 py-1 rounded-md bg-white/[0.04] border border-white/10 text-xs text-white/60">
              <Search className="h-3 w-3" />
              Search
            </div>
          </div>

          {/* Table */}
          <div className="rounded-lg border border-white/10 overflow-hidden">
            <div className="grid grid-cols-[1fr_0.8fr_1.5fr_1fr_0.9fr_0.6fr] gap-3 px-3 py-2 bg-white/[0.03] text-[10.5px] uppercase tracking-wider text-white/40">
              <span>Owner</span>
              <span>Status</span>
              <span>Campaign</span>
              <span>Channel</span>
              <span>Metric</span>
              <span className="text-right">Δ</span>
            </div>

            <div className="divide-y divide-white/5">
              {rows.map((r, i) => (
                <div
                  key={i}
                  className="grid grid-cols-[1fr_0.8fr_1.5fr_1fr_0.9fr_0.6fr] gap-3 items-center px-3 py-2 text-[12px] text-white/80"
                >
                  <span className="text-white/70 whitespace-nowrap">
                    {r.owner}
                  </span>
                  <span>
                    <span
                      className={
                        'inline-flex items-center px-2 py-0.5 rounded-md text-[10.5px] font-medium ' +
                        statusStyles[r.status]
                      }
                    >
                      {r.status}
                    </span>
                  </span>
                  <span className="truncate">{r.campaign}</span>
                  <span className="text-white/60">{r.channel}</span>
                  <span className="text-white/80">{r.metric}</span>
                  <span
                    className={
                      'text-right text-[11px] font-medium ' +
                      (r.delta.startsWith('+')
                        ? 'text-emerald-400'
                        : r.delta.startsWith('-')
                          ? 'text-rose-400'
                          : 'text-white/40')
                    }
                  >
                    {r.delta}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function NavGroup({
  label,
  items,
}: {
  label: string;
  items: { icon: typeof LayoutGrid; label: string; active?: boolean }[];
}) {
  return (
    <div className="mb-5">
      <div className="text-[10px] font-medium uppercase tracking-wider text-white/40 mb-2">
        {label}
      </div>
      <ul className="space-y-1">
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <li key={it.label}>
              <div
                className={
                  'flex items-center gap-2 px-2 py-1.5 rounded-md text-xs ' +
                  (it.active
                    ? 'bg-white/[0.06] text-white border border-white/10'
                    : 'text-white/60 hover:text-white/90')
                }
              >
                <Icon className="h-3.5 w-3.5" />
                <span>{it.label}</span>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
